import { handleResponse } from "../../client";

const PRINTER_ENDPOINT = "http://localhost:55545/api/print-pdf";
const HEALTH_ENDPOINT = "http://localhost:55545/api/health";
export const RW80L_PRINTER_NAME = "RW80L";
export const QUEUE_TICKET_PRINT_SETTINGS =
  "paper=queue-ticket-size-39mm * 72mm,landscape";
export const BROTHER_DCP_1610W_WIFI_PRINTER_NAME = "Brother DCP-1610W (Wi-Fi)";
export const A4_PRINT_SETTINGS = "paper=A4";
export const BARCODE_4B_2054A_PRINTER_NAME = "4BARCODE 4B-2054A";
export const MEDICATION_LABEL_PRINT_SETTINGS =
  "paper=drug-label-size-80mm * 50mm,landscape";
export const PATIENT_STICKER_BARCODE_PRINTER_NAME =
  "4BARCODE 4B-2025A-PATIENT-STICKER";
export const PATIENT_STICKER_PRINT_SETTINGS =
  "paper=patient-sticker-size-50mm * 25mm,landscape";
export const BROTHER_DCP_1610W_USB_PRINTER_NAME = "Brother DCP-1610W (USB)";
export const EPSON_L8050_USB_PRINTER_NAME = "EPSON L8050 Series (USB)";
export const EPSON_L8050_WIFI_PRINTER_NAME =
  "EPSONDD2C40 (L8050 Series) (Wi-Fi)";
export const PRINTER_CATALOG = Object.freeze([
  BROTHER_DCP_1610W_USB_PRINTER_NAME,
  BROTHER_DCP_1610W_WIFI_PRINTER_NAME,
  EPSON_L8050_USB_PRINTER_NAME,
  EPSON_L8050_WIFI_PRINTER_NAME,
  RW80L_PRINTER_NAME,
  BARCODE_4B_2054A_PRINTER_NAME,
  PATIENT_STICKER_BARCODE_PRINTER_NAME,
]);
export const ROLE_DEFAULT_A4_PRINTER_BY_ROLE = Object.freeze({
  doctor: EPSON_L8050_USB_PRINTER_NAME,
  "แพทย์": EPSON_L8050_USB_PRINTER_NAME,
  manager: BROTHER_DCP_1610W_WIFI_PRINTER_NAME,
  "ผู้จัดการคลินิก": BROTHER_DCP_1610W_WIFI_PRINTER_NAME,
  staff: BROTHER_DCP_1610W_WIFI_PRINTER_NAME,
  "เจ้าหน้าที่คลินิก": BROTHER_DCP_1610W_WIFI_PRINTER_NAME,
});
const PRINTER_SETTINGS_STORAGE_KEY = "kindmore_printer_settings";
export const PRINTER_SETTINGS_SOURCE_KEY = "printer_settings_source";
export const PRINTER_SETTINGS_ROLE_KEY = "printer_settings_role";
export const PRINTER_SETTINGS_SOURCE_USER = "user";
const PRINTER_SETTINGS_SOURCE_ROLE_DEFAULT = "role_default";
const A4_PRINTER_STORAGE_KEY = "a4_printer_name";
const LEGACY_A4_PRINTER_STORAGE_KEY = "certificate_printer_name";
const QUEUE_TICKET_PRINTER_STORAGE_KEY = "queue_ticket_printer_name";
const MEDICATION_LABEL_PRINTER_STORAGE_KEY = "medication_label_printer_name";
const PATIENT_STICKER_PRINTER_STORAGE_KEY = "patient_sticker_printer_name";
const A4_PRINT_SETTINGS_STORAGE_KEY = "a4_print_settings";
const LEGACY_A4_PRINT_SETTINGS_STORAGE_KEY = "certificate_print_settings";
const QUEUE_TICKET_PRINT_SETTINGS_STORAGE_KEY = "queue_ticket_print_settings";
const MEDICATION_LABEL_PRINT_SETTINGS_STORAGE_KEY =
  "medication_label_print_settings";
const PATIENT_STICKER_PRINT_SETTINGS_STORAGE_KEY =
  "patient_sticker_print_settings";

const readStoredSettings = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(PRINTER_SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    console.error("ไม่สามารถอ่านค่าเครื่องพิมพ์จาก local storage ได้", error);
    return null;
  }
};

const getStoredPrinterName = (storageKey, legacyStorageKey) => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(PRINTER_SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const storedName = [storageKey, legacyStorageKey]
      .map((key) => parsed?.[key])
      .find((value) => typeof value === "string" && value.trim());
    return storedName?.trim() || null;
  } catch (error) {
    console.error("ไม่สามารถอ่านค่าเครื่องพิมพ์จาก local storage ได้", error);
    return null;
  }
};

const getStoredPrintSettings = (storageKey, legacyStorageKey) => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(PRINTER_SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const storedSettings = [storageKey, legacyStorageKey]
      .map((key) => parsed?.[key])
      .find((value) => typeof value === "string" && value.trim());
    return storedSettings?.trim() || null;
  } catch (error) {
    console.error("ไม่สามารถอ่านค่า print settings จาก local storage ได้", error);
    return null;
  }
};

export function getRoleDefaultA4PrinterName(role) {
  return ROLE_DEFAULT_A4_PRINTER_BY_ROLE[role] || null;
}

/**
 * Apply a role default only when this browser has no user-selected printer.
 * A role default is intentionally replaceable when a user changes Settings.
 */
export function initializePrinterDefaultsForRole(role) {
  if (typeof window === "undefined") return false;

  const defaultPrinterName = getRoleDefaultA4PrinterName(role);
  if (!defaultPrinterName) return false;

  const storedSettings = readStoredSettings();
  const hasExistingA4Printer = Boolean(
    getStoredPrinterName(A4_PRINTER_STORAGE_KEY, LEGACY_A4_PRINTER_STORAGE_KEY),
  );
  const hasUserOverride =
    storedSettings?.[PRINTER_SETTINGS_SOURCE_KEY] === PRINTER_SETTINGS_SOURCE_USER ||
    (hasExistingA4Printer &&
      storedSettings?.[PRINTER_SETTINGS_SOURCE_KEY] !== PRINTER_SETTINGS_SOURCE_ROLE_DEFAULT);
  if (hasUserOverride) {
    return false;
  }

  const nextSettings = {
    ...(storedSettings || {}),
    [A4_PRINTER_STORAGE_KEY]: defaultPrinterName,
    [PRINTER_SETTINGS_SOURCE_KEY]: PRINTER_SETTINGS_SOURCE_ROLE_DEFAULT,
    [PRINTER_SETTINGS_ROLE_KEY]: role,
  };
  localStorage.setItem(PRINTER_SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings));
  return true;
}

export const resolveA4PrinterName = () =>
  getStoredPrinterName(A4_PRINTER_STORAGE_KEY, LEGACY_A4_PRINTER_STORAGE_KEY) ||
  BROTHER_DCP_1610W_WIFI_PRINTER_NAME;

export const resolveQueueTicketPrinterName = () =>
  getStoredPrinterName(QUEUE_TICKET_PRINTER_STORAGE_KEY) ||
  RW80L_PRINTER_NAME;

export const resolveMedicationLabelPrinterName = () =>
  getStoredPrinterName(MEDICATION_LABEL_PRINTER_STORAGE_KEY) ||
  BARCODE_4B_2054A_PRINTER_NAME;

export const resolvePatientStickerPrinterName = () =>
  getStoredPrinterName(PATIENT_STICKER_PRINTER_STORAGE_KEY) ||
  PATIENT_STICKER_BARCODE_PRINTER_NAME;

export const resolveA4PrintSettings = () =>
  getStoredPrintSettings(
    A4_PRINT_SETTINGS_STORAGE_KEY,
    LEGACY_A4_PRINT_SETTINGS_STORAGE_KEY,
  ) || A4_PRINT_SETTINGS;

export const resolveQueueTicketPrintSettings = () =>
  getStoredPrintSettings(QUEUE_TICKET_PRINT_SETTINGS_STORAGE_KEY) ||
  QUEUE_TICKET_PRINT_SETTINGS;

export const resolveMedicationLabelPrintSettings = () =>
  getStoredPrintSettings(MEDICATION_LABEL_PRINT_SETTINGS_STORAGE_KEY) ||
  MEDICATION_LABEL_PRINT_SETTINGS;

export const resolvePatientStickerPrintSettings = () =>
  getStoredPrintSettings(PATIENT_STICKER_PRINT_SETTINGS_STORAGE_KEY) ||
  PATIENT_STICKER_PRINT_SETTINGS;

const buildTimestampedFilename = (prefix) => {
  const now = new Date();
  const pad = (value, length = 2) => String(value).padStart(length, "0");
  const timestamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
    pad(now.getMilliseconds(), 3),
  ].join("");
  return `${prefix}-${timestamp}.pdf`;
};

const sanitizeFilenamePart = (value) => {
  if (!value) return "";
  return String(value)
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
};

const buildQueueTicketFilename = (queueNumber) => {
  const safeQueueNumber = sanitizeFilenamePart(queueNumber);
  const prefix = safeQueueNumber
    ? `queue-ticket-${safeQueueNumber}`
    : "queue-ticket";
  return buildTimestampedFilename(prefix);
};

const ensurePdfDataUri = (value) => {
  if (!value) return value;
  return value.startsWith("data:application/pdf;base64,")
    ? value
    : `data:application/pdf;base64,${value}`;
};

const appendPrintSetting = (printSettings, setting) => {
  if (!setting) return printSettings;
  if (!printSettings) return setting;
  return `${printSettings},${setting}`;
};

/**
 * Send a PDF to the local printer service.
 */
export async function printPdf({
  pdf_base64,
  filename,
  printer_name,
  print_settings,
  pages,
}) {
  if (!pdf_base64) {
    throw new Error("pdf_base64 is required");
  }
  if (!printer_name || !print_settings) {
    throw new Error("printer_name and print_settings are required");
  }

  const payload = {
    pdf_base64: ensurePdfDataUri(pdf_base64),
    filename: filename || "document.pdf",
    printer_name,
    print_settings: pages
      ? appendPrintSetting(print_settings, `${pages}x`)
      : print_settings,
  };

  try {
    const res = await fetch(PRINTER_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    return await handleResponse(res);
  } catch {
    window.dispatchEvent(new CustomEvent("printer-connection-error"));
    throw new Error(
      "ไม่สามารถเชื่อมต่อกับบริการเครื่องพิมพ์ได้ กรุณาตรวจสอบว่าโปรแกรม Kindmore Agent ทำงานอยู่",
    );
  }
}

/**
 * Check if the printer service is reachable and local network permission is granted.
 */
export async function checkPrinterConnection() {
  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 10000);

    // Try to reach the printer health endpoint
    const res = await fetch(HEALTH_ENDPOINT, {
      method: "GET",
      signal: controller.signal,
    });
    clearTimeout(id);

    return res.ok;
  } catch (error) {
    console.warn("Printer check failed:", error.message);
    return false;
  }
}

/**
 * Check local network permission status.
 */
export async function getLocalNetworkPermissionStatus() {
  if (typeof navigator === "undefined" || !navigator.permissions)
    return "unknown";

  try {
    // 'local-network-access' is the standard name for Private Network Access permission
    const status = await navigator.permissions.query({
      name: "local-network-access",
    });
    return status.state;
  } catch {
    return "unknown";
  }
}

/**
 * Convenience helper for queue ticket print jobs (pre-configured printer/settings).
 */
export function printQueueTicket({ pdf_base64, filename, queueNumber }) {
  const resolvedFilename = filename || buildQueueTicketFilename(queueNumber);
  return printPdf({
    pdf_base64,
    filename: resolvedFilename,
    printer_name: resolveQueueTicketPrinterName(),
    print_settings: resolveQueueTicketPrintSettings(),
  });
}

/**
 * Convenience helper for certificate print jobs.
 */
export function printCertificate({ pdf_base64, filename = "certificate.pdf" }) {
  return printPdf({
    pdf_base64,
    filename,
    printer_name: resolveA4PrinterName(),
    print_settings: resolveA4PrintSettings(),
  });
}

/**
 * Convenience helper for medication label print jobs.
 */
export function printMedicationLabel({
  pdf_base64,
  filename = "medication-label.pdf",
}) {
  return printPdf({
    pdf_base64,
    filename,
    printer_name: resolveMedicationLabelPrinterName(),
    print_settings: resolveMedicationLabelPrintSettings(),
  });
}

/**
 * Convenience helper for patient sticker print jobs.
 */
export function printPatientSticker({
  pdf_base64,
  filename = "patient-sticker.pdf",
  pages,
}) {
  return printPdf({
    pdf_base64,
    filename,
    printer_name: resolvePatientStickerPrinterName(),
    print_settings: resolvePatientStickerPrintSettings(),
    pages,
  });
}

/**
 * Convenience helper for receipt print jobs.
 */
export function printReceipt({ pdf_base64, filename }) {
  const resolvedFilename =
    filename && filename !== "receipt.pdf"
      ? filename
      : buildTimestampedFilename("receipt");
  return printPdf({
    pdf_base64,
    filename: resolvedFilename,
    printer_name: resolveA4PrinterName(),
    print_settings: resolveA4PrintSettings(),
  });
}

/**
 * Convenience helper for lab result print jobs.
 */
export function printLabResult({ pdf_base64, filename }) {
  const resolvedFilename =
    filename && filename !== "lab-result.pdf"
      ? filename
      : buildTimestampedFilename("lab-result");
  return printPdf({
    pdf_base64,
    filename: resolvedFilename,
    printer_name: resolveA4PrinterName(),
    print_settings: resolveA4PrintSettings(),
  });
}
